import { createSelector } from '@reduxjs/toolkit';

import { getMostRecentAnalysis } from '@paragon/client-tools';
import { SharedComponentSet } from '@paragon/contract-library';
import { naturalSortByProperty } from '@paragon/utilities';

import { RootState } from '../index';
import { getAnalysisSetsMap } from './analysisSetsSelectors';
import { timestampsRepresentWorkingChanges } from './workingChangesSelectors';

export const getSharedComponentSetHasLayout = createSelector(
  (state: RootState) => state.sharedComponentSet.projectSnapshot,

  (projectSnapshot) => !!projectSnapshot?.layout,
);

/**
 * FIXME `SharedComponentSet`s should have a dictionary of `AnalysisSet` GUID to quantity and a separate dictionary of
 * `AnalysisSet` GUID to index instead of `analysisSetGuidsWithQuantities`, which only exists as a list so TDDs have a
 * shorter link to each component (index instead of GUID). New sets cannot repeat an `AnalysisSet`, but older ones may,
 * so the quantities of repeated entries are summed.
 */
export function getAnalysisSetGuidToQuantity(sharedComponentSet: SharedComponentSet) {
  const analysisSetGuidToQuantity: { [analysisSetGuid: string]: number } = {};
  for (const { analysisSetGuid, quantity } of sharedComponentSet.analysisSetGuidsWithQuantities) {
    analysisSetGuidToQuantity[analysisSetGuid] = (analysisSetGuidToQuantity[analysisSetGuid] ?? 0) + quantity;
  }
  return analysisSetGuidToQuantity;
}

export const getComponentDesignQuantitiesForSharedComponentSet = createSelector(
  (state: RootState) => state.sharedComponentSet.sharedComponentSet,
  getAnalysisSetsMap,

  (sharedComponentSet, analysisSetsByGuid) => {
    const componentDesignQuantities: { [componentDesignGuid: string]: number } = {};
    if (!sharedComponentSet) return componentDesignQuantities;
    for (const { analysisSetGuid, quantity } of sharedComponentSet.analysisSetGuidsWithQuantities) {
      const analysisSet = analysisSetsByGuid.get(analysisSetGuid);
      if (!analysisSet) continue; // Might not be loaded yet

      const componentDesignGuid = analysisSet.componentDesign.guid;
      componentDesignQuantities[componentDesignGuid] = (componentDesignQuantities[componentDesignGuid] ?? 0) + quantity;
    }
    return componentDesignQuantities;
  },
);

export const sharedComponentSetHasOutdatedComponentsWarning = createSelector(
  (state: RootState) => state.sharedComponentSet,
  getAnalysisSetsMap,
  (state: RootState) => state.analysisHistory,
  (state: RootState) => state.componentDesignTimestamps,

  (
    sharedComponentSetState,
    analysisSetsByGuid,
    historyState,
    componentDesignTimestampsState,
  ): readonly string[] | null => {
    const { sharedComponentSet, componentDesignGuidsThatNoLongerExistInSourceProject } = sharedComponentSetState;
    if (!sharedComponentSet) return null;

    const outdatedComponentNames: string[] = [];

    for (const { analysisSetGuid } of sharedComponentSet.analysisSetGuidsWithQuantities) {
      const analysisSet = analysisSetsByGuid.get(analysisSetGuid);
      if (!analysisSet) continue; // Might not be loaded yet

      const { componentDesign } = analysisSet;
      const componentDesignGuid = componentDesign.guid;

      // Check for working changes
      const timestamps = componentDesignTimestampsState[componentDesignGuid];
      if (timestamps && timestampsRepresentWorkingChanges(timestamps)) {
        outdatedComponentNames.push(componentDesign.component.name);
        continue;
      }

      // Check for analysis not being the most recent
      const historyForComponentDesign = historyState[componentDesignGuid];
      if (historyForComponentDesign) {
        const mostRecentAnalysis = getMostRecentAnalysis(Object.values(historyForComponentDesign));
        const analysisInSharedComponentSetIsMostRecent = mostRecentAnalysis?.analysisSetGuid === analysisSetGuid;
        if (!analysisInSharedComponentSetIsMostRecent) {
          outdatedComponentNames.push(componentDesign.component.name);
          continue;
        }
      }

      // Check that component design with same GUID still exists in the backing project (has not been deleted)
      const componentDesignGuidInProject = componentDesignGuidsThatNoLongerExistInSourceProject.every(
        (componentDesignGuidNoLongerInProject) => componentDesignGuidNoLongerInProject !== componentDesignGuid,
      );
      if (!componentDesignGuidInProject) {
        outdatedComponentNames.push(componentDesign.component.name);
      }
    }

    return outdatedComponentNames.length === 0 ? null : naturalSortByProperty(outdatedComponentNames, (name) => name);
  },
);
